import { Units } from "../constants";
import { ADJECTIVES, NOUNS } from "../constants/texts";
import { PacketValueSerialType } from "../enum/PacketValueSerialType";
import { SoundByte } from "../enum/sounds";
import Packet from "../server/Packet";
import { PacketValue } from "../type/PacketValue";
import Color from "./Color";
import { Random } from "./Random";

export function GenerateId() {
    const pre: string = Random.int(0, 0xffffffff, true).toString(16).padStart(8, '0');
    const post: string = Date.now().toString(16);

    return pre + '-' + post;
}

export function clamp(v: number, min: number, max: number) {
    return Math.min(max, Math.max(min, v));
}

export function clamp01(v: number) {
    return clamp(v, 0, 1);
}

export function lerp(a: number, b: number, t: number) {
    return (b - a) * t + a;
}

export function remapToRange(value: number, fromRange: [number, number], toRange: [number, number]) {
    const t = (value - fromRange[0])/(fromRange[1] - fromRange[0]);
    return lerp(toRange[0], toRange[1], t);
}

export function isMobile() {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
}

export function round(n: number, e: number = 2) {
    return Math.floor(n * (10**e))/(10**e);
}

export function toPixels(units: number) {
    return units * Units.PIXELS_PER_UNIT;
}

export function toUnits(pixels: number) {
    return pixels / Units.PIXELS_PER_UNIT;
}

export function UnitsToMeters(units: number) {
    return units * Units.METERS_PER_UNIT;
}

export function MetersToUnits(meters: number) {
    return meters / Units.METERS_PER_UNIT;
}

export function pixelsToUnitString(pixels: number) {
    let units = toUnits(pixels);

    return toUnitString(units);
}

export function toUnitString(units: number) {
    const prefixes = ['', 'k', 'm', 'g', 't', 'p'];
    let prefixIndex = 0;

    while (units > 1000) {
        units /= 1000;
        prefixIndex++;
    }

    return `${round(units)} ${prefixes[prefixIndex]}U`;
}

export function isOutsideBounds(v: number, min: number, max: number) {
    return v < min || v > max;
}

export function selectFromPoolWithWeights(pool: {
    [key: string]: any;
    weight: number;
}[]) {
    const entries: number[] = [];

    for (let i = 0; i < pool.length; i++) {
        const item = pool[i];

        for (let j = 0; j < item.weight; j++) {
            entries.push(i);
        }
    }

    shuffle(entries, 10);

    const r = Math.floor(entries.length * Math.random());

    return pool[entries[r]];
}

export function shuffle(arr: any[], count = 1) {
    let b: any;
    for (let i = 0; i < count; i++) {
        for (let j = 0; j < arr.length; j++) {
            let r;

            do {
                r = Math.floor(arr.length * Math.random())
            } while (r === i);

            b = arr[i];
            arr[i] = arr[r];
            arr[r] = b;
        }
    }
}

export function formatTime(seconds: number) {
    let m = seconds / 60;
    let s = Math.floor(seconds) % 60;
    let h = m / 60;
    m = Math.floor(m) % 60;
    let d = Math.floor(h/24);
    h = Math.floor(h) % 24;

    return [d,h,m,s].map(v => v.toString().padStart(2, '0')).join(":");
}

export function formatTimeToMinutes(seconds: number) {
    let m = Math.floor(Math.ceil(seconds) / 60);
    let s = Math.ceil(seconds) % 60;

    return [m,s].map(v => v.toString().padStart(2, '0')).join(":");
}

export function ReadValuesFromPacket(packet: Packet) {
    const values: Record<string, any> = {};
    const numValues = packet.readUInt32();

    for (let i = 0; i < numValues; i++) {
        const key = packet.readString();
        const valueType: PacketValueSerialType = packet.readBytes(1)[0];
        let value: any;

        switch (valueType) {
            case PacketValueSerialType.Int:
                value = packet.readInt32();
                break;
            case PacketValueSerialType.UInt:
                value = packet.readUInt32();
                break;
            case PacketValueSerialType.Float:
                value = packet.readFloat32();
                break;
            case PacketValueSerialType.Bool:
                value = packet.readBool();
                break;
            case PacketValueSerialType.String:
                value = packet.readString();
                break;
            case PacketValueSerialType.Vector2:
                value = packet.readVector2();
                break;
            case PacketValueSerialType.Byte:
                value = packet.readBytes(1)[0];
                break;
            case PacketValueSerialType.Color:
                //@ts-ignore
                value = Color.fromArray(packet.readBytes(3));
                break;
            case PacketValueSerialType.Scores:
                const scores = packet.readBytes(2);
                value = {red: scores[0], blue: scores[1]};
                break;
        
            default:
                break;
        }

        values[key] = value;
    }

    return values;
}

export function AddValuesToPacket(packet: Packet, values: PacketValue[]) {

    packet.writeUInt32(values.length);
    
    for (const item of values) {
        packet.writeString(item.key);
        packet.writeBytes(item.type);

        switch (item.type) {
            case PacketValueSerialType.Int:
                packet.writeInt32(item.value);
                break;
            case PacketValueSerialType.UInt:
                packet.writeUInt32(item.value);
                break;
            case PacketValueSerialType.Float:
                packet.writeFloat32(item.value);
                break;
            case PacketValueSerialType.Bool:
                packet.writeBool(item.value);
                break;
            case PacketValueSerialType.String:
                try {
                    packet.writeString(item.value || "");
                } catch (error) {
                    console.error(error);
                    console.log(item);
                }
                break;
            case PacketValueSerialType.Vector2:
                packet.writeVector2(item.value);
                break;
            case PacketValueSerialType.Byte:
                packet.writeBytes(item.value);
                break;
            case PacketValueSerialType.Color:
                packet.writeBytes(item.value.r, item.value.g, item.value.b);
                break;
            case PacketValueSerialType.Scores:
                packet.writeBytes(item.value.red, item.value.blue);
                break;
        
            default:
                break;
        }
    }

}

export function GenerateRandomUsername() {
    const radj = ADJECTIVES[Random.int(0, ADJECTIVES.length)];
    const rnoun = NOUNS[Random.int(0, NOUNS.length)];
    return radj+rnoun;
}

export function SumArray(arr: number[]) {
    return arr.reduce((a,b) => a+b);
}

export function AvgArray(arr: number[]) {
    return SumArray(arr)/arr.length;
}

export function FitRectToScreen(w: number, h: number, sW: number, sH: number) {
    const scale = Math.min(sW/w, sH/h);
    return {
        w: w * scale,
        h: h * scale
    }
}


/**
 *
 * @param source 
 * @param target 
 * @returns {} if any element from TARGET is in SOURCE, returns false
 */
export function ContainsNone<T>(source: T[], target: T[]): boolean {
    
    for (const el of target) {
        if (source.includes(el)) return false;
    }

    return true;

}


// export function PlaySound(sound: SoundByte) {
//     const sfx =  document.createElement("audio");
//     sfx.autoplay = true;
//     sfx.onload = () => {
//         sfx.play();
//     }
//     sfx.src = sound;
// }